import { useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from './Dialog';
import { useConfirmLock } from '../../hooks/useConfirmLock';

/**
 * ConfirmDialog (T-27, PRD §4.6, voice §7.5): `role="alertdialog"`, names the
 * object and the consequence, initial focus on "ביטול", the destructive action
 * is `btn-danger` and sits ≥ 8 px away from the cancel button. Escape and the
 * scrim cancel (not while `loading`).
 */
export interface ConfirmDialogProps {
  open?: boolean;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel: ReactNode;
  cancelLabel?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  danger?: boolean;
  loading?: boolean;
  /** Extra content between the description and the actions (e.g. a cascade warning). */
  children?: ReactNode;
}

export default function ConfirmDialog({
  open = true,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  danger = false,
  loading = false,
  children,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const cancelRef = useRef<HTMLButtonElement>(null);
  // DQ-126 / DQ-175: the confirm button locks at the click itself (loading turns true a render later).
  const confirmOnce = useConfirmLock(loading, open);
  const handleConfirm = () => confirmOnce(onConfirm);

  return (
    <Dialog
      open={open}
      onClose={() => { if (!loading) onCancel(); }}
      role="alertdialog"
      title={title}
      description={description}
      size="sm"
      initialFocus={cancelRef}
      dismissible={!loading}
      hideCloseButton
      detent="medium"
      bodyClassName={children ? 'px-5 pb-1' : 'hidden'}
      footer={(
        <div className="flex w-full items-center justify-end gap-3">
          <button ref={cancelRef} type="button" className="btn btn-secondary flex-1 sm:flex-none" onClick={onCancel} disabled={loading}>
            {cancelLabel ?? t('common.cancel')}
          </button>
          <button
            type="button"
            className={`btn flex-1 sm:flex-none ${danger ? 'btn-danger' : 'btn-primary'}`}
            onClick={handleConfirm}
            disabled={loading}
            aria-busy={loading || undefined}
          >
            {confirmLabel}
          </button>
        </div>
      )}
    >
      {children}
    </Dialog>
  );
}
